import {
  cloneElement,
  isValidElement,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import type { AppEvent, PullAccount } from "../api";
import { api, isProcessedAccount, isPullAccount } from "../api";
import { AssignFromFeed } from "./AssignFromFeed";
import { ConfirmModal, ContextMenu } from "./ConfirmModal";
import { summaryFromEvent } from "./EventDetailModal";
import { PaginationBar, usePagination } from "./Pagination";
import { useToast } from "./Toast";
import { relativeTime } from "../lib/utils";
import type { WsStatus } from "../hooks/useLiveEvents";

export function statusMeta(status: WsStatus): { label: string; className: string } {
  switch (status) {
    case "open":
      return { label: "Live", className: "ok" };
    case "connecting":
      return { label: "Connecting", className: "warn" };
    case "reconnecting":
      return { label: "Reconnecting", className: "warn" };
    default:
      return { label: "Offline", className: "bad" };
  }
}

export function pullFromEvent(ev: AppEvent): PullAccount | null {
  if (isProcessedAccount(ev.payload)) return ev.payload.original;
  if (isPullAccount(ev.payload)) return ev.payload;
  return null;
}

/** Harvest login-progress stub (Activity) — not enrichment yet. */
export function isLoginProgressEvent(ev: AppEvent): boolean {
  // Enriched / failed rows keep harvest timeline + eventType=account.progress;
  // never treat those as still "in login".
  if (ev.status === "ready" || ev.status === "failed") return false;
  if (isProcessedAccount(ev.payload)) return false;
  if (ev.eventType === "account.progress") return true;
  const pull = pullFromEvent(ev);
  if (!pull) return false;
  if (pull.progressUpdate) return true;
  if (pull.type === "progress") return true;
  // First-auth / csrf fills before token beacon — still "in login", not enriching.
  if (ev.status === "processing" && !pull.token) return true;
  return false;
}

const AUTH_NON_TOKEN_AGE_MS = 10 * 60_000;

/**
 * Email+password captured, still no bearer token, credentials at least 10 minutes old.
 * Stalled email-verify / abandoned mid-auth — Octo with cookies+proxy for manual continue.
 */
export function isAuthNonTokenEvent(ev: AppEvent, nowMs = Date.now()): boolean {
  const pull = pullFromEvent(ev);
  if (!pull) return false;
  if (!pull.email?.trim() || !pull.password?.trim()) return false;
  if (pull.token?.trim()) return false;
  if (ev.status === "ready") return false;

  let credMs = NaN;
  for (const e of pull.events || []) {
    if (e.type === "password_entered") {
      const t = Date.parse(e.ts);
      if (Number.isFinite(t)) {
        credMs = t;
        break;
      }
    }
  }
  if (!Number.isFinite(credMs)) {
    for (const e of pull.events || []) {
      if (e.type === "email_entered" || e.type === "queued_auth") {
        const t = Date.parse(e.ts);
        if (Number.isFinite(t)) {
          credMs = t;
          break;
        }
      }
    }
  }
  if (!Number.isFinite(credMs)) {
    credMs = Date.parse(ev.createdAt);
  }
  if (!Number.isFinite(credMs)) return false;
  return nowMs - credMs >= AUTH_NON_TOKEN_AGE_MS;
}

export function authNonTokenAgeLabel(ev: AppEvent, nowMs = Date.now()): string {
  const pull = pullFromEvent(ev);
  let credMs = Date.parse(ev.createdAt);
  for (const e of pull?.events || []) {
    if (e.type === "password_entered") {
      const t = Date.parse(e.ts);
      if (Number.isFinite(t)) {
        credMs = t;
        break;
      }
    }
  }
  if (!Number.isFinite(credMs)) return "—";
  const mins = Math.floor((nowMs - credMs) / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m`;
}

/** Token present but GraphQL enrichment failed — needs manual attention. */
export function isEnrichmentFailure(ev: AppEvent): boolean {
  if (ev.status !== "failed") return false;
  return Boolean(pullFromEvent(ev)?.token);
}

function latestProgressMessage(ev: AppEvent): string | null {
  const pull = pullFromEvent(ev);
  const events = pull?.events;
  if (!events?.length) return null;
  const last = events[events.length - 1];
  return last?.message || last?.type || null;
}

function enrichmentFailLabel(ev: AppEvent): string {
  const note = String(ev.processNote || "").trim();
  if (!note) return "Enrichment failed";
  // Keep card readable — full note is in the detail modal.
  if (note.length > 72) return `Enrichment failed: ${note.slice(0, 72)}…`;
  return note.startsWith("Enrichment") || note.startsWith("Shape Mismatch")
    ? note
    : `Enrichment failed: ${note}`;
}

export function AccountCard({
  ev,
  isAdmin,
  onOpen,
  onToggleUsed,
  onContextMenu,
}: {
  ev: AppEvent;
  isAdmin: boolean;
  onOpen: () => void;
  onToggleUsed?: (ev: AppEvent) => void;
  onContextMenu?: (e: MouseEvent) => void;
}) {
  const summary = summaryFromEvent(ev);
  const { push } = useToast();
  const progress = isLoginProgressEvent(ev);
  const enrichFail = isEnrichmentFailure(ev);
  const ready = ev.status === "ready" || enrichFail;
  const progressMsg = progress ? latestProgressMessage(ev) : null;
  const failMsg = enrichFail ? enrichmentFailLabel(ev) : null;
  const [pending, setPending] = useState<"unassign" | "used" | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  function confirmPending() {
    if (pending === "used") {
      onToggleUsed?.(ev);
      setPending(null);
      return;
    }
    if (pending !== "unassign") return;
    setActionBusy(true);
    void api(`/api/admin/events/${ev.id}/unassign`, { method: "POST" })
      .then(() => {
        push("Unassigned", "ok");
        setPending(null);
      })
      .catch((err) =>
        push(err instanceof Error ? err.message : "Unassign failed", "error")
      )
      .finally(() => setActionBusy(false));
  }

  return (
    <article
      className={`account-card surface status-${ev.status}${progress ? " status-progress" : ""}${enrichFail ? " status-enrich-fail" : ""}`}
      onContextMenu={onContextMenu}
    >
      <button type="button" className="account-card-main" onClick={onOpen}>
        <div className="account-card-top">
          <span className="account-email truncate" title={summary.email}>
            {summary.email}
          </span>
          <time
            className="muted tiny account-time"
            title={new Date(ev.updatedAt || ev.createdAt).toLocaleString()}
          >
            {relativeTime(ev.updatedAt || ev.createdAt)}
          </time>
        </div>

        {progress ? (
          <div className="account-progress-line">
            <span className="type-pill type-progress">progress</span>
            <span className="muted tiny truncate" title={progressMsg || undefined}>
              {progressMsg || "Login in progress…"}
            </span>
          </div>
        ) : enrichFail ? (
          <div className="account-enrich-fail" title={ev.processNote || failMsg || undefined}>
            <span className="type-pill type-enrich-fail">Enrichment failed</span>
            <span className="enrich-fail-msg truncate">{failMsg}</span>
          </div>
        ) : ready && isAdmin ? (
          <div className="account-tags" title={summary.tags.join(", ") || undefined}>
            {summary.type && summary.type !== "—" && (
              <span className={`type-pill type-${summary.type}`}>{summary.type}</span>
            )}
            {summary.tags.map((t) => (
              <span key={t} className="type-pill tag-pill">
                {t}
              </span>
            ))}
            {ev.listUsed ? <span className="type-pill tag-pill">used</span> : null}
          </div>
        ) : ready && !isAdmin && ev.adminNotes?.trim() ? (
          <p className="muted tiny truncate account-notes-preview" title={ev.adminNotes}>
            {ev.adminNotes}
          </p>
        ) : ready && !isAdmin ? null : (
          <span className="muted tiny">enriching…</span>
        )}

        {ready && !progress && !enrichFail && isAdmin && (
          <div className="account-card-metrics">
            <span className="account-metric">
              <span className="muted">Bal</span> {summary.balance}
            </span>
            <span className="account-metric">
              <span className="muted">Avail</span> {summary.credit}
            </span>
            <span className="account-metric">
              <span className="muted">Limit</span> {summary.creditLimit}
            </span>
            {ev.assignedUsername && (
              <span className="muted tiny truncate account-assignee">
                → {ev.assignedUsername}
              </span>
            )}
          </div>
        )}
      </button>
      {isAdmin && ev.status === "ready" && (
        <div className="account-card-actions">
          {!ev.assignedUserId ? (
            <AssignFromFeed eventId={ev.id} label={summary.email || summary.id} />
          ) : (
            <button
              type="button"
              className="btn ghost compact"
              onClick={(e) => {
                e.stopPropagation();
                setPending("unassign");
              }}
            >
              Unassign
            </button>
          )}
          {onToggleUsed && (
            <button
              type="button"
              className="btn ghost compact"
              onClick={(e) => {
                e.stopPropagation();
                setPending("used");
              }}
            >
              {ev.listUsed ? "Mark fresh" : "Mark used"}
            </button>
          )}
        </div>
      )}
      {pending && (
        <ConfirmModal
          title={
            pending === "used"
              ? ev.listUsed
                ? "Mark fresh?"
                : "Mark used?"
              : "Unassign this account?"
          }
          body={
            pending === "used" ? (
              <p>
                {ev.listUsed ? (
                  <>
                    Move <strong>{summary.email}</strong> back to Fresh?
                  </>
                ) : (
                  <>
                    Mark <strong>{summary.email}</strong> used? It will leave Fresh
                    and show under Used.
                  </>
                )}
              </p>
            ) : (
              <p>
                Unassign <strong>{summary.email}</strong>
                {ev.assignedUsername ? (
                  <>
                    {" "}
                    from <strong>{ev.assignedUsername}</strong>
                  </>
                ) : null}
                ?
              </p>
            )
          }
          confirmLabel={
            pending === "used"
              ? ev.listUsed
                ? "Mark fresh"
                : "Mark used"
              : "Unassign"
          }
          busy={actionBusy}
          onCancel={() => !actionBusy && setPending(null)}
          onConfirm={confirmPending}
        />
      )}
    </article>
  );
}

/** Newest activity first (processed/updated), then higher id. */
export function sortEventsNewestFirst(a: AppEvent, b: AppEvent): number {
  const ta = Date.parse(a.processedAt || a.updatedAt || a.createdAt);
  const tb = Date.parse(b.processedAt || b.updatedAt || b.createdAt);
  const aMs = Number.isFinite(ta) ? ta : 0;
  const bMs = Number.isFinite(tb) ? tb : 0;
  if (aMs !== bMs) return bMs - aMs;
  return b.id - a.id;
}

export function Column({
  title,
  items,
  empty,
  isAdmin,
  onOpen,
  compact,
  pageSize = 25,
  resetKey,
  onToggleUsed,
}: {
  title: string;
  items: AppEvent[];
  empty: string;
  isAdmin: boolean;
  onOpen: (ev: AppEvent) => void;
  compact?: boolean;
  /** Max cards rendered per page (default 25). */
  pageSize?: number;
  /** Bump when filters change so paging resets. */
  resetKey?: string | number | boolean;
  onToggleUsed?: (ev: AppEvent) => void;
}) {
  const { page, setPage, pageCount, total, slice } = usePagination(
    items,
    pageSize,
    resetKey
  );

  return (
    <section className={`feed-column surface${compact ? " feed-column-compact" : ""}`}>
      <header className="feed-column-head">
        <h2>{title}</h2>
        <span className="count-pill">{total}</span>
      </header>
      <div className="feed-column-body">
        {total === 0 && <p className="muted empty-inline">{empty}</p>}
        {slice.map((ev) => (
          <HideableEvent key={ev.id} ev={ev} isAdmin={isAdmin}>
            <AccountCard
              ev={ev}
              isAdmin={isAdmin}
              onOpen={() => onOpen(ev)}
              onToggleUsed={onToggleUsed}
            />
          </HideableEvent>
        ))}
      </div>
      {total > pageSize && (
        <PaginationBar
          page={page}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          className="pagination-bar-column"
        />
      )}
    </section>
  );
}

function withRowContextMenu(
  child: ReactNode,
  open: (e: MouseEvent) => void
): ReactNode {
  if (!isValidElement(child)) return child;
  const prev = (child.props as { onContextMenu?: (e: MouseEvent) => void }).onContextMenu;
  return cloneElement(child as ReactElement<{ onContextMenu?: (e: MouseEvent) => void }>, {
    onContextMenu: (e: MouseEvent) => {
      prev?.(e);
      e.preventDefault();
      e.stopPropagation();
      open(e);
    },
  });
}

/** Admin right-click → Hide (with confirm). Wrap any list row/card. */
export function HideableEvent({
  ev,
  isAdmin,
  children,
}: {
  ev: AppEvent;
  isAdmin: boolean;
  children: ReactNode;
}) {
  const { push } = useToast();
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!isAdmin || ev.hidden) {
    return <>{children}</>;
  }

  async function doHide() {
    setBusy(true);
    try {
      const data = await api<{ event?: AppEvent }>(`/api/admin/events/${ev.id}/hide`, {
        method: "POST",
      });
      if (data.event && window.brexDesktop?.applyServerEvent) {
        void window.brexDesktop.applyServerEvent(data.event);
      }
      push("Moved to Hidden", "ok");
      setConfirm(false);
    } catch (err) {
      push(err instanceof Error ? err.message : "Hide failed", "error");
    } finally {
      setBusy(false);
    }
  }

  const label = summaryFromEvent(ev).email || ev.externalId;
  const openMenu = (e: MouseEvent) => setMenu({ x: e.clientX, y: e.clientY });

  return (
    <div
      className="hideable-event"
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        openMenu(e);
      }}
    >
      {withRowContextMenu(children, openMenu)}
      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          items={[{ id: "hide", label: "Hide from this list", danger: true }]}
          onClose={() => setMenu(null)}
          onPick={(id) => {
            setMenu(null);
            if (id === "hide") setConfirm(true);
          }}
        />
      )}
      {confirm && (
        <ConfirmModal
          title="Hide this item?"
          body={
            <p>
              Hide <strong>{label}</strong> from this list? You can restore it later from
              Hidden.
            </p>
          }
          confirmLabel="Hide"
          danger
          busy={busy}
          onCancel={() => !busy && setConfirm(false)}
          onConfirm={() => void doHide()}
        />
      )}
    </div>
  );
}
